'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import { toast } from 'sonner';

// Backend integration point: replace with /api/admin/listings?page=1&status=pending
const listingsData = [
  { id: 'lst-001', title: 'Saadiyat Beach Villa 6BR', type: 'Villa', location: 'Saadiyat Island, Abu Dhabi', price: 18500000, agent: 'Khalid Al-Rashid', agency: 'OROW Premium', status: 'Active', verified: true, listed: '14 May 2026', views: 1842 },
  { id: 'lst-002', title: 'Palm Signature Villa 7BR', type: 'Villa', location: 'Palm Jumeirah, Dubai', price: 32000000, agent: 'Nadia Petrov', agency: 'Elite Estates', status: 'Active', verified: true, listed: '02 Jun 2026', views: 2640 },
  { id: 'lst-003', title: 'Business Bay Studio', type: 'Studio', location: 'Business Bay, Dubai', price: 890000, agent: 'Ravi Kumar', agency: 'Bay Properties', status: 'Pending', verified: false, listed: '06 Jun 2026', views: 284 },
  { id: 'lst-004', title: 'Yas Bay 2BR Waterfront', type: 'Apartment', location: 'Yas Island, Abu Dhabi', price: 2950000, agent: 'Fatima Al-Zahra', agency: 'Yas Homes', status: 'Active', verified: true, listed: '20 May 2026', views: 920 },
  { id: 'lst-005', title: 'DIFC Office Suite 1800sqft', type: 'Office', location: 'DIFC, Dubai', price: 3500000, agent: 'Tariq Al-Qassim', agency: 'Commercial Hub', status: 'Under Review', verified: false, listed: '05 Jun 2026', views: 148 },
  { id: 'lst-006', title: 'Jumeirah Beach Townhouse 4BR', type: 'Townhouse', location: 'Jumeirah, Dubai', price: 7800000, agent: 'Layla Mahmoud', agency: 'Jumeirah Estates', status: 'Active', verified: true, listed: '18 May 2026', views: 1124 },
  { id: 'lst-007', title: 'The Pearl Doha 2BR', type: 'Apartment', location: 'The Pearl, Doha', price: 3800000, agent: 'Hassan Al-Thani', agency: 'Pearl Realty', status: 'Pending', verified: false, listed: '07 Jun 2026', views: 62 },
];

const statusColors: Record<string, string> = {
  Active: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  Pending: 'bg-amber-50 text-amber-700 border-amber-200',
  'Under Review': 'bg-blue-50 text-blue-700 border-blue-200',
  Suspended: 'bg-red-50 text-red-700 border-red-200',
};

export default function ListingsTable() {
  const [search, setSearch] = useState('');
  const [listings, setListings] = useState(listingsData);

  const filtered = listings.filter(l =>
    l.title.toLowerCase().includes(search.toLowerCase()) ||
    l.agent.toLowerCase().includes(search.toLowerCase()) ||
    l.location.toLowerCase().includes(search.toLowerCase())
  );

  const approveListings = (id: string) => {
    setListings(prev => prev.map(l => l.id === id ? { ...l, status: 'Active', verified: true } : l));
    toast.success('Listing approved and verified');
  };

  const suspendListing = (id: string) => {
    setListings(prev => prev.map(l => l.id === id ? { ...l, status: 'Suspended' } : l));
    toast.error('Listing suspended');
  };

  return (
    <div className="bg-card border border-border rounded-2xl shadow-card overflow-hidden">
      <div className="flex items-center justify-between p-5 border-b border-border">
        <h3 className="font-bold text-foreground text-base">Listings Management</h3>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Icon name="MagnifyingGlassIcon" size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search listings..."
              className="pl-9 pr-4 py-2 bg-muted border border-border rounded-xl text-xs text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary w-52"
            />
          </div>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/50">
            <tr>
              {['Property', 'Type', 'Price', 'Agent', 'Status', 'Verified', 'Views', 'Listed', 'Actions'].map((h) => (
                <th key={`admin-listing-header-${h}`} className="text-left py-3 px-4 text-xs font-semibold text-muted-foreground uppercase tracking-wider whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.map((listing) => (
              <tr key={listing.id} className="border-t border-border/50 hover:bg-muted/30 transition-colors group">
                <td className="py-3 px-4">
                  <div className="font-semibold text-foreground text-xs line-clamp-1 max-w-[180px]">{listing.title}</div>
                  <div className="text-muted-foreground text-xs mt-0.5 flex items-center gap-1">
                    <Icon name="MapPinIcon" size={10} />
                    <span className="line-clamp-1">{listing.location}</span>
                  </div>
                </td>
                <td className="py-3 px-4 text-xs text-foreground/80">{listing.type}</td>
                <td className="py-3 px-4 text-xs font-bold text-primary font-tabular">
                  AED {(listing.price / 1000000).toFixed(1)}M
                </td>
                <td className="py-3 px-4">
                  <div className="text-xs font-medium text-foreground">{listing.agent}</div>
                  <div className="text-xs text-muted-foreground">{listing.agency}</div>
                </td>
                <td className="py-3 px-4">
                  <span className={`text-xs font-semibold px-2 py-1 rounded-full border ${statusColors[listing.status]}`}>{listing.status}</span>
                </td>
                <td className="py-3 px-4">
                  {listing.verified ? (
                    <Icon name="CheckBadgeIcon" size={18} className="text-primary" />
                  ) : (
                    <Icon name="XCircleIcon" size={18} className="text-red-400" />
                  )}
                </td>
                <td className="py-3 px-4 text-xs text-foreground/80 font-tabular">{listing.views.toLocaleString()}</td>
                <td className="py-3 px-4 text-xs text-muted-foreground whitespace-nowrap">{listing.listed}</td>
                <td className="py-3 px-4">
                  <div className="flex items-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Link href="/property-details-page">
                      <button className="w-7 h-7 rounded-lg bg-primary/10 text-primary flex items-center justify-center hover:bg-primary hover:text-white transition-all" aria-label="View listing">
                        <Icon name="EyeIcon" size={13} />
                      </button>
                    </Link>
                    {listing.status !== 'Active' && (
                      <button
                        onClick={() => approveListings(listing.id)}
                        className="w-7 h-7 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center hover:bg-emerald-500 hover:text-white transition-all"
                        aria-label="Approve listing"
                      >
                        <Icon name="CheckIcon" size={13} />
                      </button>
                    )}
                    <button
                      onClick={() => suspendListing(listing.id)}
                      className="w-7 h-7 rounded-lg bg-red-50 text-red-500 flex items-center justify-center hover:bg-red-500 hover:text-white transition-all"
                      aria-label="Suspend listing"
                    >
                      <Icon name="NoSymbolIcon" size={13} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between px-5 py-4 border-t border-border">
        <p className="text-xs text-muted-foreground">Showing {filtered.length} of {listings.length} listings</p>
        <div className="flex items-center gap-2">
          {[1, 2, 3].map((p) => (
            <button key={`admin-page-${p}`} className={`w-8 h-8 rounded-lg text-xs font-semibold transition-all ${p === 1 ? 'bg-primary text-white' : 'border border-border text-foreground/70 hover:bg-muted'}`}>{p}</button>
          ))}
        </div>
      </div>
    </div>
  );
}